import React, { useState } from "react";
import { SbButton } from "uiCore";
import useAvailabilityStore from "Store/useAvailabilityStore";
import useRedeploymentPlanStore from "Store/useRedeploymentPlanStore";
import ShortlistConfirmModal from "../../components/ShortlistConfirmModal";

// "Shortlist for Redeployment" / "Undo Shortlist" toggle for one occupant
// in a reduction — the Reduction Detail table's row action. The confirm
// dialog itself is the shared ShortlistConfirmModal (same one Employee View's
// PersonAvailabilityButton uses).
//
// Anyone in the reduction can be shortlisted, Retained or At Risk.
// Shortlisting a Retained occupant (isRetained — ranked above the cutoff) is
// a deliberate override of the SMI ranking, so only then does the dialog ask
// for a mandatory Reason for Shortlisting; an At Risk occupant is already
// below the cutoff and needs none. Both are stored on the plan entry as
// shortlist_reason / is_retained — the backend's own check that a retained
// employee is never saved without a reason.
const ShortlistAvailabilityButton = ({ person, currentRoleJdId, isRetained = false, size = "small" }) => {
  // Deliberately the store's raw "does a record exist" check
  // (hasAvailabilityRecord), not the active-availability rule other screens
  // use (isActiveAvailabilityEntry) — this row always offers "Revert
  // recommendation" once any record exists for this employee under this
  // role, even one the Candidate Profile page's per-role revert emptied out
  // down to just its kept notifications. Clicking it here fully clears that
  // record. Same check the reduction table's Available column reads, so the
  // two agree on one row/employee.
  const hasAvailabilityRecord = useRedeploymentPlanStore((state) =>
    state.hasAvailabilityRecord(currentRoleJdId, person.id)
  );
  const markAvailable = useAvailabilityStore((state) => state.markAvailable);
  const markUnavailable = useAvailabilityStore((state) => state.markUnavailable);
  const markEmployeeAvailable = useRedeploymentPlanStore((state) => state.markEmployeeAvailable);
  const unmarkEmployeeAvailable = useRedeploymentPlanStore((state) => state.unmarkEmployeeAvailable);
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);

  const confirmMarkAvailable = ({ notifications, reason }) => {
    markAvailable(person.id, "redeployment");
    markEmployeeAvailable(currentRoleJdId, person.id, notifications, { isRetained, reason });
    setAvailabilityModalOpen(false);
  };

  // Undoes confirmMarkAvailable above — no confirm modal, since unlike
  // marking (which schedules notifications) there's nothing to configure on
  // the way back out.
  const revertRecommendation = () => {
    markUnavailable(person.id);
    unmarkEmployeeAvailable(currentRoleJdId, person.id);
  };

  // The row behind this button toggles its At Risk panel on click, and React
  // bubbles clicks from the button AND from the portaled modal (body and
  // footer) up through the React tree to that row. Stopping them at this
  // root keeps shortlisting from also expanding/collapsing the row.
  return (
    <span onClick={(e) => e.stopPropagation()}>
      {hasAvailabilityRecord ? (
        <SbButton buttonType="secondary" size={size} onClick={revertRecommendation}>
          Undo Shortlist
        </SbButton>
      ) : (
        <SbButton buttonType="primary" size={size} onClick={() => setAvailabilityModalOpen(true)}>
          Shortlist for Redeployment
        </SbButton>
      )}

      <ShortlistConfirmModal
        open={availabilityModalOpen}
        personName={person.name}
        requireReason={isRetained}
        onCancel={() => setAvailabilityModalOpen(false)}
        onConfirm={confirmMarkAvailable}
      />
    </span>
  );
};

export default ShortlistAvailabilityButton;
